import { EVENT_SITES } from '@crossbeam/itly';
import { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { sortBy, xor } from 'lodash';
import { storeToRefs } from 'pinia';
import { Ref, computed, onMounted, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { CROSSBEAM_DEFAULT_PRESET_TYPE } from '@/constants/data_shares';
import { TEMP_TEAM_DEFAULT_PRESET } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useDataSharePresetsStore,
  useFeatureFlagStore,
  useFlashesStore,
  useSourcesStore,
} from '@/stores';
import { DataSharePreset } from '@/types/data_share_presets';
import { Source, SourceMdm } from '@/types/sources';
import { sortObjectsByMdmType } from '@/utils';

type TeamDefaultEntryPoint = 'kebab' | 'preset_form' | 'delete_default';

type TeamDefaultTarget = Pick<DataSharePreset, 'id' | 'name' | 'preset_type'>;

export function useFieldPresets({
  feedId,
  sourceId,
  presetId,
  isForPartner = false,
  onClose,
  onSuccess,
  eventSite,
}: {
  feedId: number;
  sourceId?: number;
  presetId?: number;
  isForPartner?: boolean;
  onClose?: () => void;
  onSuccess?: (presetId: number, isNewPreset: boolean) => void;
  /** Event site for userDefinedSharingPreset (e.g. DATA_PRESETS_MODAL, DATA_PRESETS_TAB). */
  eventSite?: string;
}) {
  const sourcesStore = useSourcesStore();
  const { addSuccessFlash, addErrorFlash } = useFlashesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const { iteratively } = useIteratively();
  const { isAdminRoleType } = useAuth();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const { ready } = storeToRefs(dataSharePresetsStore);

  /**
   * FIELDS LOGIC
   */

  const presetName = ref<string>('');
  const crossbeamPresetId = ref<number>();
  const allSourceFieldIds = ref<number[]>([]);
  const isToggledNewPreset = ref<boolean>(false);

  const isCrossbeamPreset = computed(
    () => !!crossbeamPresetId.value && crossbeamPresetId.value === presetId,
  );
  const isTeamDefault = computed(
    () =>
      !!presetId &&
      dataSharePresetsStore.getDefaultPresetId(feedId, sourceId) === presetId,
  );
  // The backend refuses a non-admin's edits to the team default.
  const isReadOnlyPreset = computed(
    () =>
      isCrossbeamPreset.value ||
      (isTeamDefault.value && !isAdminRoleType.value),
  );

  const canSetTeamDefault = computed(
    () => hasFeatureFlag(TEMP_TEAM_DEFAULT_PRESET) && isAdminRoleType.value,
  );
  const isTeamDefaultToggled = ref(false);
  watch(
    isTeamDefault,
    (isDefault) => {
      isTeamDefaultToggled.value = isDefault;
    },
    { immediate: true },
  );
  const isBecomingTeamDefault = computed(
    () => isTeamDefaultToggled.value && !isTeamDefault.value,
  );
  // Deleting the Crossbeam preset or the team default needs an admin, who can
  // also move the default first. Read-only is about editing, not deleting.
  // The API refuses to delete whichever preset holds is_default, flag or not,
  // so this reads is_default itself rather than the flag-gated isTeamDefault.
  const isHoldingDefault = computed(
    () =>
      !!dataSharePresetsStore.allPresets.find(({ id }) => id === presetId)
        ?.is_default,
  );
  const isDeletablePreset = computed(
    () =>
      !!presetId &&
      !isOnlyPreset.value &&
      (isCrossbeamPreset.value || isHoldingDefault.value
        ? canSetTeamDefault.value
        : true),
  );
  const sortedPresets = computed(() => sortNamedPresets(feedId, sourceId));
  const isOnlyPreset = computed(() => sortedPresets.value.length <= 1);
  const currentSourceFieldIds = computed(() =>
    Object.values(sourceFieldIdsByMdm.value).flatMap((item) => item.ids.value),
  );

  const recommendedSourceFieldIds = computed(() =>
    crossbeamPresetId.value
      ? dataSharePresetsStore.getSourceFieldIdsByPresetId(
          feedId,
          sourceId,
          crossbeamPresetId.value,
        )
      : getRecommendedFieldIdsWithoutCrossbeamPreset(),
  );
  const hasDefaultPresets = computed(() => {
    const differentIdList = xor(
      recommendedSourceFieldIds.value,
      currentSourceFieldIds.value,
    );
    return differentIdList.length === 0;
  });

  const sourceFieldIdsByMdm = computed(() => {
    const syncedSources = getSourcesForPreset(feedId, sourceId).filter(
      (source) => source.sync_enabled,
    );

    const groupedWithTableAndType: Record<
      string,
      { mdm_type: SourceMdm; ids: Ref<number[]> }
    > = {};

    const sortedSources = sortObjectsByMdmType(syncedSources);

    sortedSources.forEach((source) => {
      const sourceFieldIds = source.fields.map(
        (field: { id: number }) => field.id,
      );
      const sourcePresetFieldIds = allSourceFieldIds.value.filter((id) =>
        sourceFieldIds.includes(id),
      );
      groupedWithTableAndType[source.table] = {
        mdm_type: source.mdm_type as SourceMdm,
        ids: ref(sourcePresetFieldIds),
      };
    });
    return groupedWithTableAndType;
  });

  function resetToDefault() {
    allSourceFieldIds.value = [...recommendedSourceFieldIds.value];
  }

  /**
   * Rebuilds what muncher's `update-default-preset` gives the Crossbeam preset,
   * for a team that deleted theirs: every filterable field of a CSV or Google
   * Sheets dataset, and the CRM fields `/v0.1/sources` flags in_default_preset.
   */
  function getRecommendedFieldIdsWithoutCrossbeamPreset() {
    return getSourcesForPreset(feedId, sourceId)
      .filter((source) => source.sync_enabled)
      .flatMap((source) => source.fields)
      .filter(
        (field) =>
          field.is_filterable && (!!sourceId || !!field.in_default_preset),
      )
      .map((field) => field.id)
      .filter((id) => id !== undefined);
  }

  function getFieldOptions(mdmType: SourceMdm) {
    const sourcesByFeedId = getSourcesForPreset(feedId, sourceId);
    const source = sourcesByFeedId?.find(
      (source) => source.mdm_type === mdmType,
    );
    const fields = source?.fields.filter((f) => f.is_filterable);

    const fieldOptions = fields?.map((field) => ({
      label: field.nickname,
      value: field.id,
    }));
    return fieldOptions;
  }

  onMounted(async () => {
    if (!onClose) return; // Not running it for FieldPresetTab

    // Running for the FieldPresetsModal
    await Promise.all([
      dataSharePresetsStore.readySync,
      sourcesStore.readySync,
    ]);
    crossbeamPresetId.value = dataSharePresetsStore.getCrossbeamPresetId(
      feedId,
      sourceId,
    );
    allSourceFieldIds.value = presetId
      ? dataSharePresetsStore.getSourceFieldIdsByPresetId(
          feedId,
          sourceId,
          presetId,
        )
      : [...recommendedSourceFieldIds.value];
    presetName.value = dataSharePresetsStore.getPresetName(
      feedId,
      sourceId,
      presetId || crossbeamPresetId.value,
      !presetId,
    );
  });

  /**
   * API METHODS
   */

  const isLoading = ref(false);

  function trackSetTeamDefault(
    preset: TeamDefaultTarget,
    entryPoint: TeamDefaultEntryPoint,
  ) {
    iteratively.dataSharingAction({
      event_site: eventSite ?? EVENT_SITES.DATA_PRESETS_MODAL,
      metadata: {
        action: 'set_team_default',
        preset_id: preset.id,
        preset_type: preset.preset_type,
        entry_point: entryPoint,
      },
    });
  }

  // CREATE PRESET
  async function createPreset(
    name: string,
    fieldIds: number[],
    { isDefault = false } = {},
  ) {
    isLoading.value = true;
    const isSingleShareWithoutName = isForPartner && !isToggledNewPreset.value;
    const body: GetApiPayloadByUrl<'/v0.1/data-share-presets', 'post'> = {
      name: isSingleShareWithoutName ? null : name,
      preset_type: isSingleShareWithoutName
        ? 'single_share_preset'
        : 'named_preset',
      feed_id: feedId,
      base_source_id: sourceId || null, // has only a number if csv or gsheet
      source_field_ids: fieldIds,
      is_default: isDefault || undefined,
    };

    const { data, error } = await crossbeamApi.POST(
      '/v0.1/data-share-presets',
      { body },
    );

    if (error) {
      handleErrorAndSuccess(
        `An error occurred while trying to create ${name} preset`,
        error,
      );
      return false;
    }

    if (isDefault && data) trackSetTeamDefault(data, 'preset_form');
    await redirectAndSuccess(
      isSingleShareWithoutName ? null : name,
      data,
      true,
    );
    return true;
  }

  // PATCH PRESET
  async function editPreset(
    id: number,
    name: string | null,
    fieldIds: number[],
    { isDefault = false } = {},
  ) {
    isLoading.value = true;

    const body: GetApiPayloadByUrl<'/v0.1/data-share-presets/{id}', 'patch'> = {
      name,
      source_field_ids: fieldIds,
      is_default: isDefault || undefined,
    };

    const { data, error } = await crossbeamApi.PATCH(
      '/v0.1/data-share-presets/{id}',
      { body, params: { path: { id } } },
    );

    handleErrorAndSuccess(
      `An error occurred while trying to edit ${name} preset`,
      error,
      () => {
        if (isDefault && data) trackSetTeamDefault(data, 'preset_form');
        redirectAndSuccess(name, data);
      },
    );
  }

  async function moveTeamDefault(
    preset: TeamDefaultTarget,
    entryPoint: TeamDefaultEntryPoint,
  ) {
    const response = await crossbeamApi.PUT(
      '/v0.1/data-share-presets/{id}/default',
      { params: { path: { id: preset.id } } },
    );
    if (!response.error) trackSetTeamDefault(preset, entryPoint);
    return response;
  }

  /**
   * The backend rejects PATCH on the Crossbeam preset, so its edit form saves
   * the default through here rather than through editPreset.
   */
  async function setTeamDefault(
    preset: TeamDefaultTarget,
    entryPoint: TeamDefaultEntryPoint,
  ) {
    isLoading.value = true;
    const name = preset.name || dataSharePresetsStore.crossbeamPresetLabel;
    const { data, error } = await moveTeamDefault(preset, entryPoint);

    if (error) {
      handleErrorAndSuccess(
        `An error occurred while trying to make ${name} the team default`,
        error,
      );
      return false;
    }

    await handleSuccess(data, `${name} is now your team default`);
    isLoading.value = false;
    return true;
  }

  /**
   * The backend refuses to delete the team default, so the default moves
   * first. If the delete then fails, the team keeps the new default.
   */
  async function deleteTeamDefault(
    preset: { id: number; name: string },
    newDefault: TeamDefaultTarget,
  ) {
    isLoading.value = true;
    try {
      const { error } = await moveTeamDefault(newDefault, 'delete_default');
      if (error) {
        handleErrorAndSuccess(
          `An error occurred while trying to make ${newDefault.name || dataSharePresetsStore.crossbeamPresetLabel} the team default`,
          error,
        );
        return false;
      }

      const isDeleted = await deletePreset(preset.id, preset.name);
      if (!isDeleted)
        await dataSharePresetsStore.refreshDataSharePresetsStore();
      return isDeleted;
    } catch (err) {
      captureException(err);
      addErrorFlash({
        message: `An error occurred while trying to delete ${preset.name}`,
      });
      isLoading.value = false;
      await dataSharePresetsStore.refreshDataSharePresetsStore();
      return false;
    }
  }

  // DELETE PRESET
  async function deletePreset(id: number, name: string) {
    isLoading.value = true;
    const presetType = dataSharePresetsStore.allPresets.find(
      (preset) => preset.id === id,
    )?.preset_type;
    const presetLabel =
      presetType === CROSSBEAM_DEFAULT_PRESET_TYPE
        ? dataSharePresetsStore.crossbeamPresetPhrase
        : `${name} preset`;

    const { data, error } = await crossbeamApi.DELETE(
      '/v0.1/data-share-presets/{id}',
      { params: { path: { id } } },
    );

    if (error) {
      handleErrorAndSuccess(
        `An error occurred while trying to delete ${presetLabel}`,
        error,
      );
      return false;
    }

    iteratively.dataSharingAction({
      event_site: eventSite ?? EVENT_SITES.DATA_PRESETS_MODAL,
      metadata: {
        action: 'delete_preset',
        preset_id: id,
        preset_type: presetType,
      },
    });
    await handleSuccess(data, `${presetLabel} has been successfully deleted`);
    return true;
  }

  /**
   * HELPERS
   */

  function getSourcesForPreset(feedId: number, sourceId?: number): Source[] {
    let sources = sourcesStore.getSourcesByFeedId(feedId);
    if (sourceId) {
      const baseSource = sources.find((source) => source.id === sourceId);
      sources = sources.filter(
        (source) => source.dataset_name === baseSource?.dataset_name,
      );
    }
    return sources;
  }

  function sortNamedPresets(feedId: number, sourceId?: number) {
    const filteredPresets = dataSharePresetsStore
      .getFilteredPresetsByFeeId(feedId, sourceId)
      .map((preset) => ({
        ...preset,
        name: dataSharePresetsStore.getPresetName(feedId, sourceId, preset.id),
      }));

    const filteredNamedPresets = filteredPresets.filter(
      (preset) => preset.preset_type !== 'single_share_preset',
    );

    return sortBy(filteredNamedPresets, [
      (preset) => preset.preset_type !== 'crossbeam_default', // Ensures crossbeam_default is first
      (preset) => preset.name?.toLowerCase() ?? '', // Sort the rest alphabetically
    ]);
  }

  async function redirectAndSuccess(
    name: string | null,
    data?: DataSharePreset,
    isNewPreset?: boolean,
  ) {
    if (data) {
      const presetType = data.preset_type ?? undefined;
      const metadata: {
        is_new_preset: boolean;
        id?: number;
        name?: string | null;
      } = { is_new_preset: isNewPreset ?? false };
      if (data.id != null) metadata.id = data.id;
      if (presetType === 'named_preset' && data.name != null) {
        metadata.name = data.name;
      }
      iteratively.userDefinedSharingPreset({
        event_site: eventSite ?? EVENT_SITES.DATA_PRESETS_MODAL,
        preset_type: presetType,
        metadata,
      });
    }
    await handleSuccess(
      data,
      `${name || 'Custom field selection'} has been successfully ${isNewPreset ? 'created' : 'edited'}`,
    );
    if (data?.id && onSuccess) {
      onSuccess(data.id, !!isNewPreset);
    }
    isLoading.value = false;
  }

  async function handleSuccess(data?: DataSharePreset, message?: string) {
    if (data) await dataSharePresetsStore.refreshDataSharePresetsStore();
    if (message) addSuccessFlash({ message });
    onClose?.();
  }

  function handleErrorAndSuccess(
    errorMessage: string,
    error?: { errors: string[] },
    onSuccess?: () => void,
  ) {
    if (error) {
      addErrorFlash({
        message: error.errors?.[0] || errorMessage,
      });
      isLoading.value = false;
      return;
    }
    onSuccess?.();
  }

  return {
    presetName,
    sortedPresets,
    isOnlyPreset,
    isCrossbeamPreset,
    isReadOnlyPreset,
    isTeamDefault,
    canSetTeamDefault,
    isTeamDefaultToggled,
    isBecomingTeamDefault,
    isDeletablePreset,
    hasDefaultPresets,
    sourceFieldIdsByMdm,
    currentSourceFieldIds,
    isToggledNewPreset,
    isLoading: !ready || isLoading,
    createPreset,
    setTeamDefault,
    deleteTeamDefault,
    editPreset,
    deletePreset,
    getFieldOptions,
    resetToDefault,
  };
}
